import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Button from "@/components/ui/button";
import Chip from "@/components/ui/chip";
import { LeftChevron } from "@/components/icons";
import { virtualClassroomsApi } from "@/features/education-catalog/services/virtual-classrooms.api";
import { clientEducationDetailApi } from "../services/client-education-detail.api";
import type {
  EducationDetailDto,
  VirtualClassroomDto,
} from "../types/client-education-detail.types";
import { ClassFilesSection } from "../components/sections/ClassFilesSection";
import {
  JitsiEmbed,
  extractJitsiUserFromToken,
} from "../components/sections/ContentSection/JitsiEmbed";
import { useClassroomPresence } from "../hooks/useClassroomPresence";
import { resolveClassroomJoinTarget } from "../utils/jitsi";
import styles from "./LiveClassroomPage.module.scss";

type PageState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "ready";
      education: EducationDetailDto;
      classroom: VirtualClassroomDto;
      url: string;
      token: string | null;
      isInstructor: boolean;
    };

/** Belge yüklendiğinde odadaki herkese giden Jitsi mesajı. */
const FILES_UPDATED_MESSAGE = "euas:classroom-files-updated";
/**
 * Yedek yenileme: Jitsi mesajı kaçarsa (bağlantı henüz kurulmamış, belge yönetim
 * panelinden eklenmiş) liste en geç bu sürede güncellenir.
 */
const FILES_FALLBACK_REFRESH_MS = 30_000;

function getErrorMessage(error: unknown): string | undefined {
  return (error as { response?: { data?: { message?: string } } })?.response
    ?.data?.message;
}

/**
 * Canlı sınıf sayfası (eğitim detayındaki "Katıl" yeni sekmede buraya açılır).
 *
 * Ders eskiden doğrudan Jitsi adresinde açıldığı için portal kişinin derse ne
 * zaman girip çıktığını bilemiyordu. Burada Jitsi gömülü çalışır:
 * - odaya giriş/çıkış yoklama için kaydedilir (bkz. useClassroomPresence),
 * - sınıf belgeleri yan panelde listelenir, eğitmen buradan belge yükler.
 */
export default function LiveClassroomPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { classroomId } = useParams();
  const [searchParams] = useSearchParams();
  const educationId = searchParams.get("educationId");
  const [state, setState] = useState<PageState>({ status: "loading" });
  const [joined, setJoined] = useState(false);
  const [filesOpen, setFilesOpen] = useState(true);

  useEffect(() => {
    if (!classroomId || !educationId) {
      setState({ status: "error", message: t("liveClassroom.notFound") });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });

    (async () => {
      try {
        const education = await clientEducationDetailApi.getDetail(educationId);
        const classroom = education.virtualClassrooms?.find(
          (vc) => vc.id === classroomId,
        );
        if (!classroom) {
          if (!cancelled) {
            setState({ status: "error", message: t("liveClassroom.notFound") });
          }
          return;
        }

        // Katılım kapısı (sayfa yenilendiğinde de geçerli): eğitmen dersi başlatır,
        // açıksa mevcut oturum döner; kursiyer için ders saati ve "eğitmen dersi
        // başlattı mı" kontrol edilir.
        const isInstructor = education.isInstructor === true;
        if (isInstructor) {
          await virtualClassroomsApi.startLiveSession(classroom.id);
        } else {
          await virtualClassroomsApi.getClientJitsiConfig(classroom.id);
        }

        const target = resolveClassroomJoinTarget(education, classroom);
        if (cancelled) return;
        if (!target) {
          setState({ status: "error", message: t("liveClassroom.notReady") });
          return;
        }
        setState({
          status: "ready",
          education,
          classroom,
          url: target.url,
          token: target.token,
          isInstructor,
        });
      } catch (error) {
        if (!cancelled) {
          setState({
            status: "error",
            message: getErrorMessage(error) ?? t("liveClassroom.joinFailed"),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [classroomId, educationId, t]);

  const { markJoined, markLeft } = useClassroomPresence(
    state.status === "ready" ? state.classroom.id : null,
  );

  const handleJoin = useCallback(() => {
    markJoined();
    setJoined(true);
  }, [markJoined]);

  const handleLeave = useCallback(() => {
    markLeft();
    setJoined(false);
  }, [markLeft]);

  // Görüşme bitince (ayrıl butonu) iframe'de Jitsi'nin karşılama sayfası
  // kalıyordu ("En son görüşülenler listeniz boş"); eğitim detayına dön.
  const handleClose = useCallback(() => {
    if (!educationId) return;
    navigate(
      `/dashboard/education-catalog/detail?id=${encodeURIComponent(educationId)}`,
      { replace: true },
    );
  }, [educationId, navigate]);

  // Sınıf belgeleri anlık: eğitmen yükleyince odadaki herkese Jitsi üzerinden
  // haber verilir, alanlar listeyi hemen yeniler ve yeni belgeyi bildirir.
  const jitsiApiRef = useRef<JitsiMeetExternalAPIInstance | null>(null);
  const [filesRefreshSignal, setFilesRefreshSignal] = useState(0);

  const handleApiReady = useCallback(
    (api: JitsiMeetExternalAPIInstance | null) => {
      jitsiApiRef.current = api;
    },
    [],
  );

  const handleFilesUploaded = useCallback(() => {
    jitsiApiRef.current?.executeCommand?.(
      "sendEndpointTextMessage",
      "",
      JSON.stringify({ type: FILES_UPDATED_MESSAGE }),
    );
  }, []);

  const handleEndpointTextMessage = useCallback((text: string) => {
    try {
      const message = JSON.parse(text) as { type?: string };
      if (message?.type === FILES_UPDATED_MESSAGE) {
        setFilesRefreshSignal((n) => n + 1);
      }
    } catch {
      // Başka amaçlı metin mesajı; yok say.
    }
  }, []);

  const isReady = state.status === "ready";
  useEffect(() => {
    if (!isReady) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) setFilesRefreshSignal((n) => n + 1);
    }, FILES_FALLBACK_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [isReady]);

  const backToEducation = () => {
    if (!educationId) return;
    navigate(
      `/dashboard/education-catalog/detail?id=${encodeURIComponent(educationId)}`,
    );
  };

  if (state.status !== "ready") {
    return (
      <div className={styles.page}>
        <div className={styles.message}>
          <p>
            {state.status === "loading"
              ? t("liveClassroom.loading")
              : state.message}
          </p>
          {state.status === "error" && educationId && (
            <Button variant="outlined" color="primary" onClick={backToEducation}>
              {t("liveClassroom.backToEducation")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const { education, classroom, url, token, isInstructor } = state;
  const classroomName = classroom.name?.trim();

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            type="button"
            className={styles.backButton}
            onClick={backToEducation}
            aria-label={t("liveClassroom.backToEducation")}
            title={t("liveClassroom.backToEducation")}
          >
            <LeftChevron width={20} height={20} />
          </button>
          <div className={styles.titles}>
            <h1 className={styles.title}>{classroomName || education.name}</h1>
            {classroomName && (
              <span className={styles.subtitle}>{education.name}</span>
            )}
          </div>
          {isInstructor && (
            <Chip type="default" size="small">
              {t("liveClassroom.instructorBadge")}
            </Chip>
          )}
        </div>
        <div className={styles.headerRight}>
          {joined && (
            <span className={styles.presence}>
              <span className={styles.presenceDot} />
              {t("liveClassroom.presenceRecording")}
            </span>
          )}
          <Button
            variant="text"
            color="secondary"
            onClick={() => setFilesOpen((open) => !open)}
          >
            {filesOpen
              ? t("liveClassroom.hideFiles")
              : t("liveClassroom.showFiles")}
          </Button>
        </div>
      </header>

      <div className={styles.body}>
        <JitsiEmbed
          externalUrl={url}
          token={token}
          user={extractJitsiUserFromToken(token)}
          onJoin={handleJoin}
          onLeave={handleLeave}
          onClose={handleClose}
          onApiReady={handleApiReady}
          onEndpointTextMessage={handleEndpointTextMessage}
          className={styles.meeting}
        />
        {/* Panel gizliyken de bağlı kalır: yeni belge bildirimi yine gelsin. */}
        <aside className={styles.sidePanel} hidden={!filesOpen}>
          <ClassFilesSection
            educationId={education.id}
            uploadClassrooms={
              isInstructor
                ? [{ id: classroom.id, name: classroom.name }]
                : undefined
            }
            refreshSignal={filesRefreshSignal}
            announceNewFiles
            onUploaded={handleFilesUploaded}
          />
        </aside>
      </div>
    </div>
  );
}
